/*******************************************************
* API Client
* 
* Provides a type-safe interface for making API calls to our backend.
* Handles data fetching, mutations, and caching using TanStack Query.
* 
* Key Features:
* - Type-safe API operations
* - Automatic query invalidation
* - Error handling and loading states
* - CSRF token handling
* - DPKey management for PG schema
* 
* HTTP Methods:
* - get(): HTTP GET - Fetches data with optional depth parameters
* - post(): HTTP POST - Creates new records
* - patch(): HTTP PATCH - Updates existing records
* - delete(): HTTP DELETE - Deletes records
* - fetchQuery(): Imperative data fetching with caching
* 
* Depth Parameter:
* Controls nested relation fetching using a single array where:
* - Array index position = controls how many levels of child tables to retrieve, aka collections
* - Value at that index = controls how many levels of reference (parent) tables to fetch for that child table
* 
* Examples:
* - [] or [0]: Current table only, no relations
* - [1]: Current table + 1 level of (parent) tables 
* - [2,1]: Current table + 2 levels of (parent) tables AND 1st level children (with 1 level of (parent) tables)
* - [3,5,2]: Current table + 3 levels of (parent) tables AND 1st level children (with 5 levels of (parent) tables) AND 2nd level children (with 2 levels of (parent) tables)
*
* Usage:
* ```typescript
* const api = useAPI({ tableName: 'TableName', schema: 'PG' });
* 
* // Fetch data with relation depths
* const { data } = api.useGet({ depth: [2, 1] });
* 
* // Imperative data fetching
* const data = await api.fetchQuery({ depth: [3, 1, 2] });
* 
* // HTTP Methods
* await api.post(newData);
* await api.patch(id, changes);
* await api.delete(id);
* ```
********************************************************/

//########## IMPORTS ##########
//#region IMPORTS
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useJob } from '@/src/contexts/JobContext';
import type * as Schema from '@/src/db/models/MainSchema';
import * as SchemaValues from '@/src/db/models/MainSchema';
import type * as TMSSchema from '@/src/db/models/TMSSchema';
import { entityRelationships as ENTITY_RELATIONSHIPS } from '@/src/db/models/EntityRelationships';
import type { TableModel } from '@/src/db/models/EntityRelationships.types';
import { type DeepPartial, type APIPostSuccessResponse, type APIPatchSuccessResponse, type APIGetSuccessResponse, type APIDeleteSuccessResponse } from './libs/typesLib';
import type { InferSelectModel } from 'drizzle-orm';
import { logout } from '@/src/scripts/libs/clientUtilLib';
import { getErrorContext } from '@/src/scripts/libs/clientErrorContext';
import { readCSRFToken } from '@/src/scripts/libs/csrfTokenStore';
import { isIgnoredClientError, shouldLogClientError, truncateClientErrorUrl, type ClientErrorSource } from '@/src/scripts/libs/clientErrorLogging';
import { convertTimestampValue, type TimestampDirection } from '@/src/scripts/libs/dateUtilsLib';
//#endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS


// Define what database tables we can work with
export type MainSchemaTableName = keyof typeof Schema;
export type TMSSchemaTableName = keyof typeof TMSSchema;
export type SchemaTableName = MainSchemaTableName | TMSSchemaTableName;
// We have three route schemas: AppDB (application), PG (project-specific), and TMS (upstream views)
export type DatabaseSchema = 'AppDB' | 'PG' | 'TMS';

type APIRecord<
  T extends SchemaTableName,
  D extends readonly number[] = []
> = T extends MainSchemaTableName
  ? TableModel<T, D extends readonly number[] ? [...D] : []>
  : T extends TMSSchemaTableName
    ? InferSelectModel<(typeof TMSSchema)[T]>
    : Record<string, unknown>;

// Sorting item for the new server format
export type SortItem = {
  column: string;
  order?: 'asc' | 'desc';
};

// Aggregation function types for server-side grouping
export type AggregationFunction = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'size';

// Scope-query / computed-columns types for server-side percent calcs
export interface ScopeQuery {
  table: SchemaTableName;
  filters: Record<string, string | number>;
  /** Depth for relation joins on the scope table (e.g., [1] to reach calcs views). */
  depth?: number[];
}
/**
 * Server-computed percent column definition.
 * `op: 'div'` emits a 0-100 percent-scale expression. Missing or zero
 * denominators stay NULL; a zero numerator with a positive denominator
 * remains a valid 0.00% result.
 */
export interface ComputedDivisionColumnDef {
  left: string;
  right: string;
  op: 'div';
  /**
   * Client-side only. Dot-notation path to the pre-computed column on a
   * relation (e.g. 'Prog_TrackerMeta.Prog_TrackerCalcsPipe_View.PercentCompleteMech').
   * Used when the grid is ungrouped: percents can't be derived from SUMs
   * without a GROUP BY, but the calcs view already stores the per-tracker
   * value, so we read it directly through the existing relation machinery
   * (includedColumns drives the deeper JOIN; sort/filter are translated
   * client-side to this path). When omitted, the column is hidden ungrouped.
   */
  ungroupedColumn?: string;
}

/** Server-computed value column definition. Returns the resolved aggregate directly. */
export interface ComputedValueColumnDef {
  ref: string;
  op: 'value';
}

export type ComputedColumnDef = ComputedDivisionColumnDef | ComputedValueColumnDef;
export type ComputedColumns = Record<string, ComputedColumnDef>;

// Options for making API requests
interface QueryOptions<T extends SchemaTableName> {
  schema: DatabaseSchema;
  tableName: T;
  depth?: readonly number[];
  excludedColumns?: string[];
  includedColumns?: string[];
  sort?: readonly SortItem[];
  params?: Record<string, string | number | boolean | Record<string, string | number>> | URLSearchParams;
  // Grouping and aggregation options for server-side pivot support
  groupBy?: string[];
  aggregations?: Record<string, AggregationFunction>;
  aggregateRow?: boolean;
  additionalColumns?: string[];
  scopeQuery?: ScopeQuery;
  computedColumns?: ComputedColumns;
}


// Options required for changing data (create/update/delete)
interface MutationOptions<T extends SchemaTableName> {
  schema: DatabaseSchema;
  tableName: T;
}


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
// Base URL for all API calls
const API_BASE_URL = '/api/v1';
const MAX_LOGGED_RESPONSE_BODY_LENGTH = 4000;

// Format table name for display (e.g., "UserRoles" -> "User Roles")
const formatTableName = (name: string) => {
  return name
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .trim() // Remove leading space
    .replace(/\s+/g, ' '); // Replace multiple spaces with single space
};
//#endregion


//########## FUNCTIONS ##########
//#region FUNCTIONS
// Builds the complete URL for API requests, including all parameters
function constructUrl(options: QueryOptions<SchemaTableName>, DPKey?: number | null): string {
  const { schema, tableName, depth, excludedColumns, includedColumns, sort, params, groupBy, aggregations, aggregateRow, additionalColumns, scopeQuery, computedColumns } = options;
  const url = new URL(`${API_BASE_URL}/${schema}/${tableName}`, window.location.origin);
  const searchParams = new URLSearchParams();

  // Add DPKey (project key) for PG/TMS schema
  if (schema !== 'AppDB' && DPKey) {
    searchParams.append('DPKey', DPKey.toString());
  }

  if (Array.isArray(depth) && depth.length) {
    searchParams.append('depth', JSON.stringify(depth));
  }

  // Add excludedColumns as JSON array if provided
  if (Array.isArray(excludedColumns) && excludedColumns.length) {
    searchParams.append('excludedColumns', JSON.stringify(excludedColumns));
  }

  // Add includedColumns as JSON array if provided
  if (Array.isArray(includedColumns) && includedColumns.length) {
    searchParams.append('includedColumns', JSON.stringify(includedColumns));
  }

  // Add sort as JSON array if provided
  if (Array.isArray(sort) && sort.length) {
    searchParams.append('sort', JSON.stringify(sort));
  }

  // Add grouping and aggregation parameters for server-side pivot
  if (Array.isArray(groupBy) && groupBy.length) {
    searchParams.append('groupBy', JSON.stringify(groupBy));
  }

  if (aggregations && Object.keys(aggregations).length > 0) {
    searchParams.append('aggregations', JSON.stringify(aggregations));
  }

  if (aggregateRow) {
    searchParams.append('aggregateRow', '1');
  }

  if (Array.isArray(additionalColumns) && additionalColumns.length) {
    searchParams.append('additionalColumns', JSON.stringify(additionalColumns));
  }

  if (scopeQuery) {
    searchParams.append('scopeQuery', JSON.stringify(scopeQuery));
  }

  if (computedColumns && Object.keys(computedColumns).length > 0) {
    searchParams.append('computedColumns', JSON.stringify(computedColumns));
  }

  // Add any additional parameters
  if (params) {
    if (params instanceof URLSearchParams) {
      params.forEach((value, key) => {
        searchParams.append(key, value);
      });
    } else {
      Object.entries(params).forEach(([key, value]) => {
        if (value == null) return; // skip undefined/null params
        // Special handling for 'or' parameter - serialize objects to JSON
        if (key === 'or' && typeof value === 'object' && !Array.isArray(value)) {
          searchParams.append(key, JSON.stringify(value));
        } else if (typeof value === 'object') {
          // Skip other objects or stringify them as needed
          searchParams.append(key, JSON.stringify(value));
        } else {
          searchParams.append(key, value.toString());
        }
      });
    }
  }

  url.search = searchParams.toString();
  return url.toString();
}

// Timestamp utilities
// Detect timestamp columns via Drizzle schema at runtime and convert values
/**
 * Returns the set of column names for a table that are MySQL TIMESTAMP columns.
 * Uses the Drizzle schema at runtime
 */
function getTimestampColumnsForTable(tableName: SchemaTableName): Set<string> {
  const table: unknown = (SchemaValues as Record<string, unknown>)[tableName as string];
  const cols = new Set<string>();
  if (table && typeof table === 'object') {
    for (const [key, val] of Object.entries(table as Record<string, unknown>)) {
      if (val && typeof val === 'object' && 'columnType' in (val as Record<string, unknown>)) {
        const columnType = (val as Record<string, unknown>).columnType;
        if (typeof columnType === 'string' && columnType.includes('Timestamp')) {
          cols.add(key);
        }
      }
    }
  }
  return cols;
}

function convertRecordTimestamps<TRec extends Record<string, unknown>>(tableName: SchemaTableName, record: TRec, tsCols: Set<string> | undefined, direction: TimestampDirection): TRec {
  const timestampCols = tsCols ?? getTimestampColumnsForTable(tableName);
  if (timestampCols.size === 0) return record;
  const out: Record<string, unknown> = { ...record };
  for (const key of timestampCols) {
    if (key in out) {
      out[key] = convertTimestampValue(out[key], direction);
    }
  }
  return out as TRec;
}

// Build relation name -> child table name map using EntityRelationships
const RELATION_CHILD_TABLE_MAP: Record<string, Map<string, SchemaTableName>> = {};

function getRelationNameToChildTableMap(tableName: SchemaTableName): Map<string, SchemaTableName> {
  const cached = RELATION_CHILD_TABLE_MAP[tableName as string];
  if (cached) return cached;

  const relDef = (ENTITY_RELATIONSHIPS as Record<string, { relations?: Array<{ name: string; childTable: unknown }> }>)[tableName as string];
  const map = new Map<string, SchemaTableName>();
  if (relDef && Array.isArray(relDef.relations)) {
    for (const r of relDef.relations) {
      const childObj = r.childTable;
      // Find the schema export name whose value is the child table object
      for (const [key, val] of Object.entries(SchemaValues as Record<string, unknown>)) {
        if (val === childObj) {
          map.set(r.name, key as SchemaTableName);
          break;
        }
      }
    }
  }
  RELATION_CHILD_TABLE_MAP[tableName as string] = map;
  return map;
}

/**
 * Converts all TIMESTAMP fields for a record (and nested related records) in the given direction.
 * Uses EntityRelationships to resolve nested relation property names to their child table names.
 */
function convertRecordTimestampsDeep(tableName: SchemaTableName, record: Record<string, unknown>, direction: TimestampDirection): Record<string, unknown> {
  const tsCols = getTimestampColumnsForTable(tableName);
  const base = convertRecordTimestamps(tableName, record, tsCols, direction);
  const relationMap = getRelationNameToChildTableMap(tableName);
  for (const [key, value] of Object.entries(base)) {
    if (!value) continue;
    if (relationMap.has(key)) {
      const childTableName = relationMap.get(key)!;
      if (Array.isArray(value)) {
        base[key] = value.map((v: unknown) => (v && typeof v === 'object') ? convertRecordTimestampsDeep(childTableName, v as Record<string, unknown>, direction) : v);
      } else if (typeof value === 'object') {
        base[key] = convertRecordTimestampsDeep(childTableName, value as Record<string, unknown>, direction);
      }
    }
  }
  return base;
}

// Gets the security token needed for API requests
export function getCSRFToken(): string {
  const token = readCSRFToken();
  if (!token) {
    console.log('[LOGOUT BOUNCE] getCSRFToken: No in-memory CSRF token, redirecting to /Login');
    window.location.href = '/Login';
    return '';
  }
  return token;
}

// Check if an error response is an authentication/session error that should trigger logout
function isAuthError(response: Response, errorData: unknown): boolean {
  // 401 always means authentication failure
  if (response.status === 401) {
    console.log('401 is an auth error, logging user out');
    return true;
  }

  // For 403, check if it's an auth error from preRoute (not business logic)
  if (response.status === 403) {
    const errorObj = errorData as { error?: string };
    const errorText = errorObj?.error || '';

    // These specific error messages come from preRouteSession and indicate auth failures
    // Note: 'Invalid App Role' and 'Invalid Discipline Role' are NOT included here
    // because those are authorization errors (wrong permissions), not authentication errors.
    // Authorization errors should show an error screen, not log the user out.
    const authErrorMessages = [
      'Invalid Session',
      'Invalid CSRF Token',
      'Invalid DPKey'
    ];
    const isAuthError = authErrorMessages.some(msg => errorText.includes(msg));
    if (isAuthError) {
      console.log(`${errorText} is an auth error, logging user out`);
    }
    return isAuthError;

  }
  return false;
}

// Same error within the dedupe window is only logged once per tab
const clientErrorLastLogged = new Map<string, number>();

// Logs client errors to the Log_ClientErrors table (fire-and-forget)
// ExtraData.source records where the error was caught (api | boundary | window | promise)
export function logClientError(url: string, errorText: string, extraData?: Record<string, unknown> & { source?: ClientErrorSource }): void {
  if (isIgnoredClientError(errorText)) return;
  if (!shouldLogClientError(`${extraData?.source ?? ''}|${url}|${errorText}`, clientErrorLastLogged)) return;

  // Fire-and-forget: don't await, don't block error handling
  fetch('/api/v1/AppDB/Log_ClientErrors', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'CSRFToken': getCSRFToken(),
    },
    body: JSON.stringify({
      URL: truncateClientErrorUrl(url),
      ErrorText: errorText,
      ExtraData: extraData ?? null,
      SessionData: getErrorContext(),
    }),
  }).catch(() => {
    // Silently fail - don't let logging errors affect the application
    // Network errors or logging failures won't disrupt the user experience
  });
}

// Error thrown from handleAPIError that carries the HTTP status so callers
// (e.g. TanStack Query's retry predicate) can decide whether to retry.
export class APIError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'APIError';
    this.status = status;
  }
}

type APIErrorContext = {
  tableName: string;
  method?: string;
};

function parseErrorResponseBody(rawBody: string): unknown {
  if (!rawBody) return null;

  try {
    return JSON.parse(rawBody);
  } catch {
    return rawBody;
  }
}

function getErrorMessage(errorData: unknown): string | null {
  if (typeof errorData === 'string') {
    const message = errorData.trim();
    // Proxy errors commonly return an HTML error page. Keep that body in
    // ExtraData, but do not surface markup as the user-facing message.
    return message && !message.startsWith('<') ? message : null;
  }

  if (!errorData || typeof errorData !== 'object') return null;

  const errorObject = errorData as { error?: unknown; message?: unknown; errorText?: unknown };
  const message = errorObject.error ?? errorObject.message ?? errorObject.errorText;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}

// Handles API errors and redirects to login if unauthorized
export async function handleAPIError(
  response: Response,
  context?: APIErrorContext
): Promise<never> {
  const tableDisplay = context ? formatTableName(context.tableName) : 'record';

  // Read the response once so non-JSON proxy/upstream errors remain available
  // for diagnostics while JSON responses can still drive auth and UI handling.
  const rawResponseBody = await response.text();
  const errorData = parseErrorResponseBody(rawResponseBody);

  // Check if this is an authentication/session error that should log the user out
  if (isAuthError(response, errorData)) {
    console.log(`[LOGOUT BOUNCE] handleAPIError: Auth error from ${response.url} (status ${response.status}), calling logout()`, errorData);
    await logout();

    // This throw will never be reached due to the redirect, but satisfies TypeScript
    throw new Error('Session expired - Redirecting to login');
  }

  // For non-auth errors, log them to the client error table
  // Extract error message for logging
  let errorMessage = `Failed to process ${tableDisplay.toLowerCase()} operation`;
  const serverMessage = getErrorMessage(errorData);

  // For other common HTTP errors, provide specific messages
  if (response.status === 404) {
    errorMessage = `The requested ${tableDisplay.toLowerCase()} was not found`;
  } else if (response.status === 409) {
    errorMessage = serverMessage ?? `The ${tableDisplay.toLowerCase()} operation conflicted with existing data`;
  } else if (response.status === 413) {
    // Payload Too Large — server responses should remain actionable when possible.
    errorMessage = serverMessage ?? 'Result set is too large to return.';
  } else if (serverMessage) {
    errorMessage = serverMessage;
  } else {
    const statusDescription = response.statusText ? ` ${response.statusText}` : '';
    errorMessage = `Failed to process ${tableDisplay.toLowerCase()} operation: HTTP ${response.status}${statusDescription}`;
  }

  const responseBody = rawResponseBody.slice(0, MAX_LOGGED_RESPONSE_BODY_LENGTH);
  logClientError(response.url, errorMessage, {
    source: 'api',
    tableName: context?.tableName ?? null,
    method: context?.method ?? 'UNKNOWN',
    status: response.status,
    statusText: response.statusText || null,
    requestId: response.headers.get('x-request-id'),
    contentType: response.headers.get('content-type'),
    responseBody: responseBody || null,
    responseBodyTruncated: rawResponseBody.length > MAX_LOGGED_RESPONSE_BODY_LENGTH,
  });

  throw new APIError(errorMessage, response.status);
}
//#endregion


//########## MAIN LOGIC ##########
//#region MAIN LOGIC
// Main hook that creates an API client for a specific table
export function useAPI<T extends SchemaTableName>({ tableName, schema }: MutationOptions<T>) {
  // Get tools for managing cache and current job
  const queryClient = useQueryClient();
  const { ActiveJobID } = useJob();
  // Generic controls may construct the hook before they have an API-backed
  // data source. Never let that transient/placeholder state become a request
  // to /api/v1/undefined/undefined (or an empty table route).
  const hasConfiguredEndpoint = Boolean(schema && tableName);

  // Setup function for creating new records (POST)
  const createMutation = useMutation({
    // Function that does the actual API call
    mutationFn: async ({ data, onDuplicateKeyUpdate = false, params }: {
      data: DeepPartial<APIRecord<T, [5, 5, 5, 5, 5]>> | DeepPartial<APIRecord<T, [5, 5, 5, 5, 5]>>[],
      onDuplicateKeyUpdate?: boolean,
      lazy?: boolean,
      params?: Record<string, string | number | boolean> | URLSearchParams
    }) => {
      if (schema !== 'AppDB' && !ActiveJobID) {
        throw new Error('Cannot perform operation: No active job selected');
      }

      // Make the API call
      const url = constructUrl({ schema, tableName, params }, ActiveJobID);

      // Normalize payload: trim strings and convert timestamp fields to UTC
      const dataToSend = Array.isArray(data)
        ? data.map(item => {
          const trimmed = Object.fromEntries(Object.entries(item as Record<string, unknown>).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]));
          return convertRecordTimestampsDeep(tableName, trimmed, 'toUTC');
        })
        : convertRecordTimestampsDeep(
          tableName,
          {
            ...data,
            ...Object.fromEntries(
              Object.entries(data as Record<string, unknown>).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
            )
          },
          'toUTC'
        );

      const urlWithParams = new URL(url);
      if (onDuplicateKeyUpdate) {
        urlWithParams.searchParams.append('onDuplicateKeyUpdate', 'true');
      }
      const response = await fetch(urlWithParams.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CSRFToken': getCSRFToken(),
          ...(schema !== 'AppDB' && ActiveJobID && { 'DPKey': ActiveJobID.toString() })
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        await handleAPIError(response, { tableName, method: 'POST' });
      }

      return response.json() as unknown as APIPostSuccessResponse<APIRecord<T, [5, 5, 5, 5, 5]>[]>;
    },
    // After successful create, refresh the data and show success toast
    onSuccess: (_data, variables) => {
      // Only invalidate queries if the operation wasn't lazy
      if (!variables.lazy) {
        void queryClient.invalidateQueries({ queryKey: [tableName, schema] });
      }
    },
    onError: (error: Error) => {
      console.error(error);
    },
  });

  // Setup function for updating records (PATCH)
  const updateMutation = useMutation<
    APIPatchSuccessResponse, // Type of data returned by mutationFn
    Error, // Type of error
    { id: number | string | number[] | string[], data: Partial<APIRecord<T>>, lazy?: boolean } // Type of variables passed to mutationFn
  >({
    mutationFn: async ({ id, data }) => { // Destructure lazy here, default is false
      if (schema !== 'AppDB' && !ActiveJobID) {
        throw new Error('Cannot perform operation: No active job selected');
      }

      const url = constructUrl({ schema, tableName }, ActiveJobID);

      // Normalize payload: trim strings and convert timestamp fields to UTC
      const dataToSend = convertRecordTimestampsDeep(
        tableName,
        {
          ...data,
          ...Object.fromEntries(Object.entries(data).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]))
        },
        'toUTC'
      );

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'CSRFToken': getCSRFToken(),
          ...(schema !== 'AppDB' && ActiveJobID && { 'DPKey': ActiveJobID.toString() })
        },
        body: JSON.stringify({
          primaryKeys: Array.isArray(id) ? id : [id],
          updateData: dataToSend
        }),
      });

      if (!response.ok) {
        await handleAPIError(response, { tableName, method: 'PATCH' });
      }

      // Return the parsed JSON response
      return response.json() as unknown as APIPatchSuccessResponse;
    },
    onSuccess: (_data, variables) => { // Access variables passed to mutationFn
      // Only invalidate queries if the operation wasn't lazy
      if (!variables.lazy) {
        void queryClient.invalidateQueries({ queryKey: [tableName, schema] });
      }
    },
    onError: (error: Error) => {
      console.error(error);
    },
  });

  // Setup function for deleting records (DELETE)
  const deleteMutation = useMutation<
    APIDeleteSuccessResponse, // Type of data returned by mutationFn
    Error, // Type of error
    { id: number | string | (number | string)[], lazy?: boolean } // Type of variables passed to mutationFn
  >({
    mutationFn: async ({ id }: { id: number | string | (number | string)[] }) => {
      if (schema !== 'AppDB' && !ActiveJobID) {
        throw new Error('Cannot perform operation: No active job selected');
      }

      const url = constructUrl({ schema, tableName }, ActiveJobID);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'CSRFToken': getCSRFToken(),
          ...(schema !== 'AppDB' && ActiveJobID && { 'DPKey': ActiveJobID.toString() })
        },
        body: JSON.stringify(Array.isArray(id) ? id : [id])
      });

      if (!response.ok) {
        await handleAPIError(response, { tableName, method: 'DELETE' });
      }

      return response.json() as unknown as APIDeleteSuccessResponse;
    },
    onSuccess: (_data, variables) => {
      // Only invalidate queries if the operation wasn't lazy
      if (!variables.lazy) {
        void queryClient.invalidateQueries({ queryKey: [tableName, schema] });
      }
    },
    onError: (error: Error) => {
      console.error(error);
    },
  });

  // Create a fetchQuery function that can be called imperatively
  const fetchQuery = async <D extends readonly number[] = []>(
    options?: {
      /**
       * Nested relation traversal depth. Each array element specifies how deeply
       * to fetch one-to-one relationships. Array length describes the number of one-to-many relationships to retrieve.
       * 
       * @example
       * [] & [0]   // (index 0, value null or 0) Fetch current table only, no relations
       * 
       * [1]        // (index 0, value 1)Fetch current table with 1 level of "Ref Tables" AKA "Parent Table", AKA one-to-one and many-to-one relations
       * 
       * [2,1]      // (index 0, value 2)Fetch current table with 2 levels of "Ref Tables" AKA "Parent and Grandpartent Tables" AKA one-to-one and many-to-one relations
       *             + (index 1, value 1) ALSO Fetch 1st level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 1 level of "Ref Tables"
       *
       * [3,3,3]    // (index 0, value 3)Fetch current table with 3 levels of "Ref Tables" AKA "Parent, Grandpartent, Great-Grandparent Tables" AKA one-to-one and many-to-one relations
       *             + (index 1, value 3) ALSO Fetch 1st level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 3 levels of "Ref Tables"
       *             + (index 2, value 3) ALSO Fetch 2nd level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 3 levels of "Ref Tables"
       */
      depth?: D;
      excludedColumns?: string[];
      includedColumns?: string[];
      sort?: readonly SortItem[];
      params?: Record<string, string | number | Record<string, string | number>> | URLSearchParams;
      distinctColumn?: string;
      // Grouping and aggregation for server-side pivot support
      groupBy?: string[];
      aggregations?: Record<string, AggregationFunction>;
      aggregateRow?: boolean;
      additionalColumns?: string[];
      scopeQuery?: ScopeQuery;
      computedColumns?: ComputedColumns;
      /** How long (ms) cached data is considered fresh. When set, fetchQuery returns cached data without a network call if the cache entry is younger than this value. */
      staleTime?: number;
    }
  ): Promise<APIGetSuccessResponse<APIRecord<T, D>>> => {
    if (schema !== 'AppDB' && !ActiveJobID) {
      throw new Error('Cannot perform operation: No active job selected');
    }

    // Merge parameters: if distinctColumn is provided, add it as a query parameter
    let params: Record<string, string | number | Record<string, string | number>> = {};
    if (options?.params) {
      if (options.params instanceof URLSearchParams) {
        options.params.forEach((val, key) => {
          params[key] = val;
        });
      } else {
        params = { ...options.params };
      }
    }
    if (options?.distinctColumn) {
      params.distinctColumns = options.distinctColumn;
    }

    // Build the query key - same structure as in useGet for cache consistency
    const queryKey = [
      tableName,
      schema,
      options?.depth,
      schema !== 'AppDB' ? ActiveJobID : undefined,
      params,
      options?.distinctColumn,
      options?.sort,
      options?.excludedColumns,
      options?.includedColumns,
      options?.groupBy,
      options?.aggregations,
      options?.aggregateRow,
      options?.additionalColumns,
      options?.scopeQuery,
      options?.computedColumns,
    ] as const;

    // Use queryClient.fetchQuery to perform the request and cache the result
    return queryClient.fetchQuery({
      queryKey,
      staleTime: options?.staleTime,
      queryFn: async () => {
        const url = constructUrl(
          {
            tableName,
            schema,
            depth: options?.depth ? Array.from(options.depth) : undefined,
            excludedColumns: options?.excludedColumns,
            includedColumns: options?.includedColumns,
            sort: options?.sort,
            params,
            groupBy: options?.groupBy,
            aggregations: options?.aggregations,
            aggregateRow: options?.aggregateRow,
            additionalColumns: options?.additionalColumns,
            scopeQuery: options?.scopeQuery,
            computedColumns: options?.computedColumns,
          },
          ActiveJobID
        );
        const response = await fetch(url);
        if (!response.ok) {
          await handleAPIError(response, { tableName, method: 'GET' });
        }
        const json = await response.json() as APIGetSuccessResponse<APIRecord<T, D>>;
        const converted = Array.isArray(json.data)
          ? json.data.map((r) => convertRecordTimestampsDeep(tableName, r as unknown as Record<string, unknown>, 'fromUTC'))
          : json.data;
        return { ...json, data: converted } as APIGetSuccessResponse<APIRecord<T, D>>;
      }
    });
  };

  // Overloaded get() method with explicit tuple type handling
  function useGet<const D extends readonly number[] = []>(
    options?: {
      /**
       * Nested relation traversal depth. Each array element specifies how deeply
       * to fetch one-to-one relationships. Array length describes the number of one-to-many relationships to retrieve.
       *
       * @example
       * [] & [0]   // (index 0, value null or 0) Fetch current table only, no relations
       *
       * [1]        // (index 0, value 1)Fetch current table with 1 level of "Ref Tables" AKA "Parent Table", AKA one-to-one and many-to-one relations
       *
       * [2,1]      // (index 0, value 2)Fetch current table with 2 levels of "Ref Tables" AKA "Parent and Grandpartent Tables" AKA one-to-one and many-to-one relations
       *             + (index 1, value 1) ALSO Fetch 1st level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 1 level of "Ref Tables"
       *
       * [3,3,3]    // (index 0, value 3)Fetch current table with 3 levels of "Ref Tables" AKA "Parent, Grandpartent, Great-Grandparent Tables" AKA one-to-one and many-to-one relations
       *             + (index 1, value 3) ALSO Fetch 1st level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 3 levels of "Ref Tables"
       *             + (index 2, value 3) ALSO Fetch 2nd level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 3 levels of "Ref Tables"
       */
      depth?: D;
      excludedColumns?: string[];
      includedColumns?: string[];
      sort?: readonly SortItem[];
      params?: Record<string, string | number | Record<string, string | number>> | URLSearchParams;
      distinctColumn?: string;
      enabled?: boolean;
      keepPreviousData?: boolean;
      staleTime?: number;
      refetchOnMount?: boolean | 'always';
      // Grouping and aggregation for server-side pivot support
      groupBy?: string[];
      aggregations?: Record<string, AggregationFunction>;
      aggregateRow?: boolean;
      additionalColumns?: string[];
      scopeQuery?: ScopeQuery;
      computedColumns?: ComputedColumns;
    }
  ): ReturnType<typeof useQuery<APIGetSuccessResponse<APIRecord<T, D>>>>;

  // Implementation signature
  function useGet<const D extends readonly number[] = []>(
    options?: {
      depth?: D;
      excludedColumns?: string[];
      includedColumns?: string[];
      sort?: readonly SortItem[];
      params?: Record<string, string | number | Record<string, string | number>> | URLSearchParams;
      distinctColumn?: string;
      enabled?: boolean;
      keepPreviousData?: boolean;
      staleTime?: number;
      refetchOnMount?: boolean | 'always';
      groupBy?: string[];
      aggregations?: Record<string, AggregationFunction>;
      aggregateRow?: boolean;
      additionalColumns?: string[];
      scopeQuery?: ScopeQuery;
      computedColumns?: ComputedColumns;
    }
  ) {
    return useQuery({
      queryKey: [
        tableName,
        schema,
        options?.depth,
        schema !== 'AppDB' ? ActiveJobID : undefined,
        options?.params,
        options?.distinctColumn,
        options?.sort,
        options?.excludedColumns,
        options?.includedColumns,
        options?.groupBy,
        options?.aggregations,
        options?.aggregateRow,
        options?.additionalColumns,
        options?.scopeQuery,
        options?.computedColumns,
      ] as const,
      queryFn: async () => {
        // Merge parameters: if distinctColumn is provided, add it as a query parameter.
        let params: Record<string, string | number | Record<string, string | number>> = {};
        if (options?.params) {
          if (options.params instanceof URLSearchParams) {
            options.params.forEach((val, key) => {
              params[key] = val;
            });
          } else {
            params = { ...options.params };
          }
        }
        if (options?.distinctColumn) {
          params.distinctColumns = options.distinctColumn;
        }
        const url = constructUrl(
          {
            tableName,
            schema,
            depth: options?.depth ? Array.from(options.depth) : undefined,
            excludedColumns: options?.excludedColumns,
            includedColumns: options?.includedColumns,
            sort: options?.sort,
            params,
            groupBy: options?.groupBy,
            aggregations: options?.aggregations,
            aggregateRow: options?.aggregateRow,
            additionalColumns: options?.additionalColumns,
            scopeQuery: options?.scopeQuery,
            computedColumns: options?.computedColumns,
          },
          ActiveJobID
        );
        const response = await fetch(url);
        if (!response.ok) {
          await handleAPIError(response, { tableName, method: 'GET' });
        }
        const json = await response.json() as APIGetSuccessResponse<APIRecord<T, D>>;
        const converted = Array.isArray(json.data)
          ? json.data.map((r) => convertRecordTimestampsDeep(tableName, r as unknown as Record<string, unknown>, 'fromUTC'))
          : json.data;
        return { ...json, data: converted } as APIGetSuccessResponse<APIRecord<T, D>>;
      },
      enabled: hasConfiguredEndpoint &&
        (options?.enabled !== undefined ? options.enabled : true) &&
        (schema === 'AppDB' || !!ActiveJobID),
      placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
      refetchOnWindowFocus: false,
      staleTime: options?.staleTime,
      refetchOnMount: options?.refetchOnMount,
    });
  }

  // Infinite get() method
  function useInfiniteGet<const D extends readonly number[] = []>(
    options?: {
      /**
       * Nested relation traversal depth. Each array element specifies how deeply
       * to fetch one-to-one relationships. Array length describes the number of one-to-many relationships to retrieve.
       * 
       * @example
       * [] & [0]   // (index 0, value null or 0) Fetch current table only, no relations
       * 
       * [1]        // (index 0, value 1)Fetch current table with 1 level of "Ref Tables" AKA "Parent Table", AKA one-to-one and many-to-one relations
       * 
       * [2,1]      // (index 0, value 2)Fetch current table with 2 levels of "Ref Tables" AKA "Parent and Grandpartent Tables" AKA one-to-one and many-to-one relations
       *             + (index 1, value 1) ALSO Fetch 1st level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 1 level of "Ref Tables"
       *
       * [3,3,3]    // (index 0, value 3)Fetch current table with 3 levels of "Ref Tables" AKA "Parent, Grandpartent, Great-Grandparent Tables" AKA one-to-one and many-to-one relations
       *             + (index 1, value 3) ALSO Fetch 1st level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 3 levels of "Ref Tables"
       *             + (index 2, value 3) ALSO Fetch 2nd level of "Collection Tables" AKA "Child Tables" AKA one-to-many relations.  Child table will also fetch 3 levels of "Ref Tables"
       */
      depth?: D;
      excludedColumns?: string[];
      includedColumns?: string[];
      sort?: readonly SortItem[];
      params?: Record<string, string | number | Record<string, string | number>> | URLSearchParams;
      enabled?: boolean;
      keepPreviousData?: boolean;
      initialPageParam?: number;
    }
  ) {
    return useInfiniteQuery({
      queryKey: [
        tableName,
        schema,
        'infinite',
        options?.depth,
        ActiveJobID,
        options?.params,
        options?.sort,
        options?.excludedColumns,
        options?.includedColumns
      ] as const,
      enabled: hasConfiguredEndpoint &&
        (options?.enabled !== undefined ? options.enabled : true) &&
        (schema === 'AppDB' || !!ActiveJobID),
      initialPageParam: options?.initialPageParam ?? 1,
      queryFn: async ({ pageParam }) => {
        // Merge parameters
        let params: Record<string, string | number | Record<string, string | number>> = {};
        if (options?.params) {
          if (options.params instanceof URLSearchParams) {
            options.params.forEach((val, key) => {
              params[key] = val;
            });
          } else {
            params = { ...options.params };
          }
        }
        // Add/override page param
        params.page = Number(pageParam ?? 1);

        const url = constructUrl(
          {
            tableName,
            schema,
            depth: options?.depth ? Array.from(options.depth) : undefined,
            excludedColumns: options?.excludedColumns,
            includedColumns: options?.includedColumns,
            sort: options?.sort,
            params
          },
          ActiveJobID
        );
        const response = await fetch(url);
        if (!response.ok) {
          await handleAPIError(response, { tableName, method: 'GET' });
        }
        const json = await response.json() as APIGetSuccessResponse<APIRecord<T, D>>;
        const converted = Array.isArray(json.data)
          ? json.data.map((r) => convertRecordTimestampsDeep(tableName, r as unknown as Record<string, unknown>, 'fromUTC'))
          : json.data;
        return { ...json, data: converted } as APIGetSuccessResponse<APIRecord<T, D>>;
      },
      getNextPageParam: (lastPage) => {
        const page = lastPage.page || 1;
        const pageSize = lastPage.pageSize || 50;
        const total = lastPage.recordsTotal ?? lastPage.totalCount ?? 0;
        const pagesTotal = lastPage.pagesTotal ?? (total > 0 ? Math.ceil(total / pageSize) : page);
        const next = page + 1;
        return next <= pagesTotal ? next : undefined;
      },
      placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
      refetchOnWindowFocus: false,
    });
  }

  // Return a memoized API client object — reference only changes when table/schema/job or mutation status changes.
  return useMemo(() => ({
    useGet,
    useInfiniteGet,
    fetchQuery,
    post: (data: DeepPartial<APIRecord<T, [5, 5, 5, 5, 5]>> | DeepPartial<APIRecord<T, [5, 5, 5, 5, 5]>>[], onDuplicateKeyUpdate?: boolean, lazy?: boolean, params?: Record<string, string | number | boolean> | URLSearchParams) =>
      createMutation.mutateAsync({ data, onDuplicateKeyUpdate, lazy, params }),
    patch: (id: number | string | number[] | string[], data: Partial<APIRecord<T>>, lazy?: boolean) =>
      updateMutation.mutateAsync({ id, data, lazy }),
    delete: (id: number | string | (number | string)[], lazy?: boolean) =>
      deleteMutation.mutateAsync({ id, lazy }),
    status: {
      isPosting: createMutation.isPending,
      isPatching: updateMutation.isPending,
      isDeleting: deleteMutation.isPending,
      postError: createMutation.error,
      patchError: updateMutation.error,
      deleteError: deleteMutation.error
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tableName, schema, ActiveJobID,
       createMutation.isPending, updateMutation.isPending, deleteMutation.isPending,
       createMutation.error, updateMutation.error, deleteMutation.error]);
}
//#endregion
